/**
 * Visitor-country detection for the public landing page.
 *
 * POS has no multi-country backend, so detection is purely client-side and sends nothing anywhere
 * (POS-I187: the old ipapi.co lookup posted every visitor's address to a third party and was
 * blocked by CORS):
 *   1. A manual override persisted in localStorage (set via the country selector) always wins.
 *   2. Otherwise the browser's own time zone picks a listed country (countryFromTimeZone).
 *   3. An unknown zone defaults to Pakistan so the FBR-centric experience is shown to the primary
 *      audience.
 */
import { useCallback, useSyncExternalStore } from 'react';
import { browserTimeZone, countryFromTimeZone } from '@/lib/country-from-timezone';

const STORAGE_KEY = 'pos.landing.country';

/** Country options offered in the landing-page selector. */
export const COUNTRY_OPTIONS: { code: string; label: string }[] = [
  { code: 'PK', label: 'Pakistan' },
  { code: 'AE', label: 'United Arab Emirates' },
  { code: 'SA', label: 'Saudi Arabia' },
  { code: 'GB', label: 'United Kingdom' },
  { code: 'US', label: 'United States' },
  { code: 'IN', label: 'India' },
  { code: 'BD', label: 'Bangladesh' },
  { code: 'Other', label: 'Other / Rest of world' },
];

function readOverride(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function subscribe(callback: () => void): () => void {
  window.addEventListener('storage', callback);
  window.addEventListener('pos-country-change', callback);
  return () => {
    window.removeEventListener('storage', callback);
    window.removeEventListener('pos-country-change', callback);
  };
}

export interface UseCountryResult {
  /** Resolved country code (override → browser time zone → default `PK`). */
  country: string;
  /** Whether the current country is Pakistan (controls FBR content). */
  isPakistan: boolean;
  /** Whether the visitor manually picked a country. */
  isOverridden: boolean;
  /** Persist a manual country choice (`null` clears the override). */
  setCountry: (code: string | null) => void;
}

export function useCountry(): UseCountryResult {
  const override = useSyncExternalStore(subscribe, readOverride, () => null);
  const country = (override ?? countryFromTimeZone(browserTimeZone())).toUpperCase();

  const setCountry = useCallback((code: string | null) => {
    try {
      if (code) localStorage.setItem(STORAGE_KEY, code.toUpperCase());
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore storage failures (private mode, quota) */
    }
    window.dispatchEvent(new Event('pos-country-change'));
  }, []);

  return {
    country,
    isPakistan: country === 'PK',
    isOverridden: override !== null,
    setCountry,
  };
}
